import React from 'react';

import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import { Button, Spinner } from 'react-bootstrap';
import { faExternalLinkAlt, faImages, faSearchPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ProductType } from 'lib/enums/ProductType';
import { BrandSkuMapperMfrToDpReviewAction } from 'lib/enums/BrandSkuMapperMfrToDpEnums';
import ChannelBadges from 'components/brand_sku_mapper_mfr_to_dp/ChannelBadges';
import { ProductImage } from 'components/brand_sku_mapper_shared/ProductImage';
import { galleryStyles } from 'components/brand_sku_mapper_shared/galleryStyles';
import { countImages } from 'components/brand_sku_mapper_shared/imageHelpers';
import { styles } from 'components/brand_sku_mapper_mfr_to_dp/sharedStyles';
import {
  MISMATCH_SIGNAL_LABELS,
  candBrandLine,
  candVendorSkuLine,
  pct,
  tierColor,
} from 'components/brand_sku_mapper_mfr_to_dp/cardHelpers';

// A single DP candidate tile. `mode` is 'approved' (shows un-approve) or 'pending' (selectable).
function CandidateCard({
  candidate: c,
  mode,
  isSelected,
  reviewingAction,
  onToggleSelect,
  onUnapprove,
  onZoom,
}) {
  const isApprovedCard = mode === 'approved';
  const vp = c.vendor_product || {};
  const imageCount = countImages(vp.thumbnail_url, vp.image_urls);
  return (
    <div
      className={css(
        styles.candCard,
        isSelected && styles.candCardSelected,
        isApprovedCard && styles.candCardMatched,
      )}
      onClick={() => (isApprovedCard ? undefined : onToggleSelect(c))}
      onKeyDown={e => {
        if (e.target !== e.currentTarget || isApprovedCard) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleSelect(c);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {isApprovedCard ? (
        <Button
          size="sm"
          className={css(styles.unapproveBtn)}
          disabled={reviewingAction !== null}
          onClick={e => {
            e.stopPropagation();
            onUnapprove(c.vendor_product_id);
          }}
        >
          {reviewingAction === 'unapprove' ? (
            <Spinner animation="border" size="sm" />
          ) : (
            '✕ Un-approve'
          )}
        </Button>
      ) : (
        <div className={css(styles.rankBadge)}>#{c.rank}</div>
      )}
      <div className={css(styles.candTopRightRow)}>
        {c.manufacturer_mismatch && (
          <span
            className={css(
              styles.mismatchBadge,
              !c.mismatch_signal && styles.mismatchBadgeNoSignal,
            )}
            title="Different manufacturer than the MFR product"
          >
            ⚠ Mfr
          </span>
        )}
        <span
          className={css(styles.tierPillFloating)}
          style={{ backgroundColor: tierColor(c.tier) }}
        >
          {pct(c.calibrated_confidence)} · {c.tier || '—'}
        </span>
        <span className={css(styles.candStatusPill)}>
          {`sts:${(vp.status || 'null').toLowerCase()}`}
        </span>
      </div>
      {isApprovedCard && c.approved_by && (
        <div
          className={css(styles.approvedByLine)}
          title={c.approved_at ? `Approved ${c.approved_at}` : undefined}
        >
          ✓ {c.approved_by}
        </div>
      )}
      <div className={css(styles.candImageWrap)}>
        <div className={css(galleryStyles.candImageInner)}>
          <ProductImage
            src={vp.thumbnail_url}
            imgClassName={css(styles.candImage)}
            placeholderClassName={css(styles.candImagePlaceholder)}
          />
          {imageCount > 0 && (
            <div className={css(galleryStyles.imageCountBadge)} title={`${imageCount} image(s)`}>
              <FontAwesomeIcon icon={faImages} /> {imageCount}
            </div>
          )}
        </div>
      </div>
      <div className={css(styles.candName)}>{vp.name || `DP #${c.vendor_product_id}`}</div>
      <div className={css(styles.candIconRail)}>
        <button
          type="button"
          className={css(styles.candIconButton)}
          onClick={e => {
            e.stopPropagation();
            onZoom(c.vendor_product_id);
          }}
          aria-label="Zoom in on candidate"
          title="Zoom in"
        >
          <FontAwesomeIcon icon={faSearchPlus} />
        </button>
        {vp.global_product_id && vp.id ? (
          <a
            href={`/global-product-detail/${vp.global_product_id}/${vp.id}/${ProductType.DISTRIBUTOR}`}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.candIconButton)}
            onClick={e => e.stopPropagation()}
            aria-label="Open distributor product page"
            title="Open product page"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </a>
        ) : (
          <span
            className={css(styles.candIconButton, styles.candIconButtonDisabled)}
            title="Product page unavailable"
            role="button"
            tabIndex={0}
            aria-disabled="true"
            aria-label="Product page unavailable"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </span>
        )}
      </div>
      <div className={css(styles.candBottomBlock)}>
        <div className={css(styles.mutedText)}>{candBrandLine(vp)}</div>
        <div className={css(styles.mutedText)}>{vp.pack_size || '—'}</div>
        <div className={css(styles.mutedText)}>{candVendorSkuLine(vp)}</div>
        <ChannelBadges channels={c.channels} />
        {c.manufacturer_mismatch && (
          <div
            className={css(
              styles.overrideReason,
              !c.mismatch_signal && styles.overrideReasonNoSignal,
            )}
          >
            {c.mismatch_signal
              ? `Mfr mismatch · justified by ${
                  MISMATCH_SIGNAL_LABELS[c.mismatch_signal] || c.mismatch_signal
                }`
              : 'Mfr mismatch · no signal — reviewer override'}
          </div>
        )}
      </div>
    </div>
  );
}

CandidateCard.propTypes = {
  candidate: PropTypes.shape({
    vendor_product_id: PropTypes.number,
    vendor_product: PropTypes.object,
    rank: PropTypes.number,
    tier: PropTypes.string,
    calibrated_confidence: PropTypes.number,
    channels: PropTypes.arrayOf(PropTypes.string),
    manufacturer_mismatch: PropTypes.bool,
    mismatch_signal: PropTypes.string,
    approved_by: PropTypes.string,
    approved_at: PropTypes.string,
  }).isRequired,
  mode: PropTypes.oneOf(['approved', 'pending']).isRequired,
  isSelected: PropTypes.bool,
  reviewingAction: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(Object.values(BrandSkuMapperMfrToDpReviewAction)),
  ]),
  onToggleSelect: PropTypes.func.isRequired,
  onUnapprove: PropTypes.func.isRequired,
  onZoom: PropTypes.func.isRequired,
};

CandidateCard.defaultProps = {
  isSelected: false,
  reviewingAction: null,
};

export default CandidateCard;
